import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../api/api';
import ReferenceInfoDialog from '../components/ReferenceInfoDialog';

/* ─── constants ─── */
const SERIES_OPTS  = ['Primary'];
const PRICE_LISTS  = ['Price List 01', 'Last Evaluated Price', 'Average Price', 'Standard Price'];
const STATUS_COLORS = {
  Draft:     'bg-[#F0F4F3] text-[#6B7F7F]',
  Open:      'bg-[#E8F4FE] text-[#2184F2]',
  Closed:    'bg-[#EDF2F7] text-[#4A6FA5]',
  Cancelled: 'bg-[#FDF0EE] text-[#C4574B]',
};
const today = () => new Date().toISOString().slice(0, 10);

const blankLine = () => ({
  _key: Date.now() + Math.random(),
  item_id: '', item_no: '', item_description: '',
  from_warehouse_id: '', from_wh_code: '',
  to_warehouse_id: '', to_wh_code: '',
  quantity: '1.0000', uom_code: '', uom_name: '',
  product_group: '', period: '', requester: '', request_ref: '', report: '',
});

const blankDoc = {
  request_number: '', series: 'Primary',
  posting_date: today(), due_date: today(), document_date: today(),
  referenced_document: '',
  bp_code: '', bp_name: '', contact_person: '', ship_to: '',
  from_warehouse_id: '', from_wh_code: '',
  to_warehouse_id: '', to_wh_code: '',
  price_list: 'Price List 01',
  sales_employee: '', pick_and_pack_remarks: '',
  journal_remarks: 'Inventory Transfer Request', remarks: '',
  status: 'Draft',
};

/* ─── styles ─── */
const inp = `w-full h-[28px] px-2 text-xs border border-sap-border rounded bg-white focus:outline-none focus:ring-1 focus:ring-sap-blue/40 focus:border-sap-blue`;
const sel = `${inp} appearance-none`;
const lbl = 'block text-[10px] font-medium text-sap-textLight mb-0.5 whitespace-nowrap';

/* ─────────────── YellowArrow ─────────────── */
const YellowArrow = ({ onClick, title }) => (
  <button type="button" onClick={onClick} title={title}
    className="flex-shrink-0 flex items-center justify-center w-5 h-5 hover:opacity-80 transition-opacity">
    <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
      <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
    </svg>
  </button>
);

/* ─────────────── WarehousePickerDialog ─────────────── */
const WarehousePickerDialog = ({ warehouses, currentWhId, label, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const filtered = warehouses.filter(w =>
    !search ||
    w.wh_code.toLowerCase().includes(search.toLowerCase()) ||
    w.wh_name.toLowerCase().includes(search.toLowerCase())
  );
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[520px] max-h-[420px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        {/* Title */}
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Warehouses</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        {/* Find */}
        <div className="flex items-center gap-2 px-4 py-2 border-b border-sap-border">
          <span className="text-xs text-sap-textLight w-8">Find</span>
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        {/* Table */}
        <div className="overflow-y-auto flex-1">
          <table className="w-full text-xs">
            <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
              <tr>
                <th className="text-center px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-10">#</th>
                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Warehouse Code</th>
                <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Warehouse Name</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((w, i) => {
                const isCurrent = String(w.wh_id) === String(currentWhId);
                return (
                  <tr key={w.wh_id} onClick={() => onSelect(w)}
                    className={`border-t border-sap-border/20 cursor-pointer transition-colors
                      ${isCurrent ? 'bg-amber-100 font-semibold' : 'hover:bg-sap-blue/5'}`}>
                    <td className="text-center px-3 py-1.5 text-sap-textLight">{i + 1}</td>
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{w.wh_code}</td>
                    <td className="px-3 py-1.5 text-sap-text">{w.wh_name}</td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={3} className="px-3 py-4 text-center text-sap-textLight">No warehouses found</td></tr>
              )}
            </tbody>
          </table>
        </div>
        {/* Buttons */}
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-sap-border bg-gray-50 rounded-b-xl">
          <button onClick={() => filtered.length > 0 && onSelect(filtered[0])}
            className="px-5 py-1.5 text-xs bg-sap-blue text-white rounded hover:bg-sap-blueDark font-semibold transition-colors">
            Choose
          </button>
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs border border-sap-border rounded text-sap-text hover:bg-gray-100 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── SalesEmployeePickerDialog ─────────────── */
const SalesEmployeePickerDialog = ({ current, onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [employees, setEmployees] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [highlighted, setHighlighted] = React.useState(current || '');

  useEffect(() => {
    api.get('/sales-employees')
      .then(d => { setEmployees(d || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const filtered = employees.filter(e =>
    !search || e.employee_name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[520px] max-h-[480px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">List of Sales Employee</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <div className="flex items-center gap-2">
            <span className="text-xs text-sap-text whitespace-nowrap font-medium">Find</span>
            <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
              className="flex-1 h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
          </div>
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-6 text-center text-xs text-sap-textLight">Loading…</div> : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-10">#</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Sales Employee Name</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-40">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((emp, idx) => {
                  const isHL = emp.employee_name === highlighted;
                  return (
                    <tr key={emp.id}
                      onClick={() => setHighlighted(emp.employee_name)}
                      onDoubleClick={() => { onSelect(emp.employee_name); onClose(); }}
                      className={`cursor-pointer border-t border-sap-border/20 transition-colors ${
                        isHL ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'
                      }`}>
                      <td className="px-3 py-1.5 text-sap-textLight">{idx + 1}</td>
                      <td className="px-3 py-1.5 text-sap-text">{emp.employee_name}</td>
                      <td className="px-3 py-1.5 text-sap-textLight">{emp.remarks || ''}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-sap-textLight">No results</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="flex items-center gap-2 px-4 py-2.5 border-t border-sap-border bg-gray-50 rounded-b-xl">
          <button
            onClick={() => { if (highlighted) { onSelect(highlighted); onClose(); } }}
            className="px-5 py-1.5 text-xs bg-sap-blue text-white rounded-lg font-semibold hover:bg-sap-blueDark transition-colors">
            Choose
          </button>
          <button onClick={onClose}
            className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 font-semibold transition-colors">
            Cancel
          </button>
          <span className="ml-auto text-[10px] text-sap-textLight">{filtered.length} employee{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>
    </div>
  );
};

/* ─────────────── ItemPickerDialog ─────────────── */
const ItemPickerDialog = ({ onSelect, onClose }) => {
  const [search, setSearch] = React.useState('');
  const [items, setItems]   = React.useState([]);
  const [loading, setLoading] = React.useState(false);

  const doSearch = useCallback(async (q) => {
    setLoading(true);
    try {
      const rows = await api.get(`/inventory-transfer-requests/items?search=${encodeURIComponent(q)}`);
      setItems(rows || []);
    } catch { setItems([]); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { doSearch(''); }, [doSearch]);
  useEffect(() => {
    const t = setTimeout(() => doSearch(search), 300);
    return () => clearTimeout(t);
  }, [search, doSearch]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/30" onClick={onClose}>
      <div className="bg-white rounded-xl shadow-2xl border border-sap-border w-[700px] max-h-[500px] flex flex-col"
        onClick={e => e.stopPropagation()}>
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Select Item</span>
          <button onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input autoFocus value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search item no. or description…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? <div className="py-6 text-center text-xs text-sap-textLight">Searching…</div> : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-36">Item No.</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider">Description</th>
                  <th className="text-left px-3 py-1.5 text-[10px] font-semibold text-sap-textLight uppercase tracking-wider w-20">UoM</th>
                </tr>
              </thead>
              <tbody>
                {items.map(it => (
                  <tr key={it.item_id} onClick={() => onSelect(it)}
                    className="border-t border-sap-border/20 hover:bg-sap-blue/5 cursor-pointer transition-colors">
                    <td className="px-3 py-1.5 text-sap-blue font-mono font-semibold">{it.item_no}</td>
                    <td className="px-3 py-1.5 text-sap-text">{it.description}</td>
                    <td className="px-3 py-1.5 text-sap-textLight">{it.uom || '—'}</td>
                  </tr>
                ))}
                {items.length === 0 && !loading && (
                  <tr><td colSpan={3} className="px-3 py-4 text-center text-sap-textLight">No items found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl text-[10px] text-sap-textLight">
          Click a row to select
        </div>
      </div>
    </div>
  );
};

/* ════════════════════════════════════════
   InventoryTransferRequestDetail
════════════════════════════════════════ */
export default function InventoryTransferRequestDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isNew = id === 'new';

  const [tab, setTab]           = useState('Contents');
  const [form, setForm]         = useState({ ...blankDoc });
  const [lines, setLines]       = useState([blankLine()]);
  const [warehouses, setWarehouses] = useState([]);
  const [loading, setLoading]   = useState(!isNew);
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');
  const [successMsg, setSuccessMsg] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [selectedAttIdx, setSelectedAttIdx] = useState(null);
  const attInputRef = React.useRef(null);

  // Picker states
  const [whPicker, setWhPicker]     = useState(null); // { field, lineIdx? }
  const [itemPickerIdx, setItemPickerIdx] = useState(null);
  const [showSalesPicker, setShowSalesPicker] = useState(false);
  const [showRefInfo, setShowRefInfo]       = useState(false);
  const [refsTo, setRefsTo] = useState([]);
  const [refsBy, setRefsBy] = useState([]);

  /* load warehouses */
  useEffect(() => {
    api.get('/inventory-transfer-requests/warehouses')
      .then(d => setWarehouses(d || [])).catch(() => {});
  }, []);

  /* load doc */
  useEffect(() => {
    if (isNew) return;
    setLoading(true);
    api.get(`/inventory-transfer-requests/${id}`)
      .then(d => {
        const { lines: rawLines, ...doc } = d;
        setForm({
          ...blankDoc, ...doc,
          posting_date:  doc.posting_date  ? doc.posting_date.slice(0, 10)  : today(),
          due_date:      doc.due_date      ? doc.due_date.slice(0, 10)      : today(),
          document_date: doc.document_date ? doc.document_date.slice(0, 10) : today(),
          from_warehouse_id: doc.from_warehouse_id || '',
          from_wh_code:      doc.from_wh_code || '',
          to_warehouse_id:   doc.to_warehouse_id || '',
          to_wh_code:        doc.to_wh_code || '',
        });
        setLines(rawLines && rawLines.length > 0
          ? rawLines.map(l => ({ ...blankLine(), ...l,
              from_wh_code: l.from_wh_code || '',
              to_wh_code:   l.to_wh_code   || '',
            }))
          : [blankLine()]
        );
        setLoading(false);
      })
      .catch(() => { setError('Failed to load'); setLoading(false); });
  }, [id, isNew]); // eslint-disable-line

  const setField = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const updateLine = (idx, k, v) => setLines(ls => ls.map((l, i) => i === idx ? { ...l, [k]: v } : l));
  const addLine    = () => setLines(ls => [...ls, blankLine()]);
  const removeLine = (idx) => setLines(ls => ls.filter((_, i) => i !== idx));

  /* warehouse picker resolve */
  const handleWhSelect = (wh) => {
    if (!whPicker) return;
    const { field, lineIdx } = whPicker;
    if (field === 'from') {
      setField('from_warehouse_id', String(wh.wh_id));
      setField('from_wh_code', wh.wh_code);
    } else if (field === 'to') {
      setField('to_warehouse_id', String(wh.wh_id));
      setField('to_wh_code', wh.wh_code);
    } else if (field === 'line_from') {
      updateLine(lineIdx, 'from_warehouse_id', String(wh.wh_id));
      updateLine(lineIdx, 'from_wh_code', wh.wh_code);
    } else if (field === 'line_to') {
      updateLine(lineIdx, 'to_warehouse_id', String(wh.wh_id));
      updateLine(lineIdx, 'to_wh_code', wh.wh_code);
    }
    setWhPicker(null);
  };

  /* item picker resolve */
  const handleItemSelect = (item) => {
    if (itemPickerIdx === null) return;
    setLines(ls => ls.map((l, i) => i === itemPickerIdx ? {
      ...l,
      item_id: item.item_id, item_no: item.item_no,
      item_description: item.description,
      uom_code: item.uom || '', uom_name: item.uom || '',
      from_warehouse_id: l.from_warehouse_id || form.from_warehouse_id,
      from_wh_code:      l.from_wh_code      || form.from_wh_code,
      to_warehouse_id:   l.to_warehouse_id   || form.to_warehouse_id,
      to_wh_code:        l.to_wh_code        || form.to_wh_code,
    } : l));
    setItemPickerIdx(null);
  };

  /* save */
  const handleSave = async (statusOverride) => {
    const validLines = lines.filter(l => l.item_id || l.item_no);
    setSaving(true); setError(''); setSuccessMsg('');
    try {
      const payload = {
        ...form,
        from_warehouse_id: form.from_warehouse_id || null,
        to_warehouse_id:   form.to_warehouse_id   || null,
        status: statusOverride || form.status,
        lines: validLines.map(l => ({
          item_id:          l.item_id || null,
          item_no:          l.item_no || null,
          item_description: l.item_description || null,
          from_warehouse_id: l.from_warehouse_id || form.from_warehouse_id || null,
          to_warehouse_id:   l.to_warehouse_id   || form.to_warehouse_id   || null,
          quantity:   parseFloat(l.quantity) || 1,
          uom_code:   l.uom_code || null,
          uom_name:   l.uom_name || null,
          product_group: l.product_group || null,
          period:     l.period || null,
          requester:  l.requester || null,
          request_ref: l.request_ref || null,
          report:     l.report || null,
        })),
      };
      if (isNew) {
        const r = await api.post('/inventory-transfer-requests', payload);
        setSuccessMsg('Created!');
        setTimeout(() => navigate(`/inventory/transfer-request/${r.request_id}`), 700);
      } else {
        await api.put(`/inventory-transfer-requests/${id}`, payload);
        setSuccessMsg('Saved');
        if (statusOverride) setField('status', statusOverride);
        setTimeout(() => setSuccessMsg(''), 2500);
      }
    } catch (e) {
      setError(e?.message || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const getPickerCurrentId = () => {
    if (!whPicker) return null;
    if (whPicker.field === 'from') return form.from_warehouse_id;
    if (whPicker.field === 'to')   return form.to_warehouse_id;
    if (whPicker.field === 'line_from') return lines[whPicker.lineIdx]?.from_warehouse_id;
    if (whPicker.field === 'line_to')   return lines[whPicker.lineIdx]?.to_warehouse_id;
    return null;
  };

  if (loading) return <div className="py-20 text-center text-xs text-sap-textLight">Loading…</div>;
  const isReadOnly = form.status === 'Closed' || form.status === 'Cancelled';

  /* ══════════════════════ RENDER ══════════════════════ */
  return (
    <div className="space-y-2 pb-6">
      {/* Breadcrumb */}
      <div className="text-[10px] text-sap-textLight flex items-center gap-1">
        <button onClick={() => navigate('/inventory/transfer-requests')}
          className="hover:text-sap-blue transition-colors">Inventory Transfer Request</button>
        <span>›</span>
        <span className="text-sap-text font-medium">{isNew ? 'New' : form.request_number}</span>
      </div>

      {/* Title bar */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <h1 className="text-sm font-bold text-sap-text">Inventory Transfer Request</h1>
          {!isNew && (
            <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${STATUS_COLORS[form.status] || ''}`}>
              {form.status}
            </span>
          )}
        </div>
        <div className="flex items-center gap-1.5">
          {error      && <span className="text-xs text-red-500">{error}</span>}
          {successMsg && <span className="text-xs text-emerald-600 font-semibold">{successMsg}</span>}
        </div>
      </div>

      {/* Document card */}
      <div className="bg-white rounded-2xl shadow-sm border border-sap-border/30 overflow-hidden">

        {/* ── Header ── */}
        <div className="grid grid-cols-2 gap-0 border-b border-sap-border/30">

          {/* Left */}
          <div className="p-4 space-y-2 border-r border-sap-border/30">
            <div>
              <label className={lbl}>Business Partner</label>
              <input value={form.bp_code} onChange={e => setField('bp_code', e.target.value)}
                disabled={isReadOnly} placeholder="BP Code" className={inp} />
            </div>
            <div>
              <label className={lbl}>Name</label>
              <input value={form.bp_name} onChange={e => setField('bp_name', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
            <div>
              <label className={lbl}>Contact Person</label>
              <input value={form.contact_person} onChange={e => setField('contact_person', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
            <div>
              <label className={lbl}>Ship To</label>
              <select value={form.ship_to} onChange={e => setField('ship_to', e.target.value)}
                disabled={isReadOnly} className={sel}>
                <option value=""></option>
                {form.ship_to && <option value={form.ship_to}>{form.ship_to}</option>}
              </select>
            </div>
            {/* text area below Ship To matching SAP */}
            <div>
              <textarea value={form.remarks_header || ''}
                onChange={e => setField('remarks_header', e.target.value)}
                disabled={isReadOnly} rows={3}
                className="w-full px-2 py-1 text-xs border border-sap-border rounded bg-white focus:outline-none focus:border-sap-blue resize-none" />
            </div>
            <div className="pt-1">
              <label className={lbl}>Price List</label>
              <select value={form.price_list} onChange={e => setField('price_list', e.target.value)}
                disabled={isReadOnly} className={sel}>
                {PRICE_LISTS.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>

          {/* Right */}
          <div className="p-4 space-y-2">
            {/* No. + Series + Status */}
            <div className="flex gap-2">
              <div className="flex-1 min-w-0">
                <label className={lbl}>No.</label>
                <div className="flex items-center gap-1">
                  <select value={form.series} onChange={e => setField('series', e.target.value)}
                    disabled={isReadOnly} className={`${sel} w-[80px] min-w-[80px] max-w-[80px]`}>
                    {SERIES_OPTS.map(s => <option key={s} value={s}>{s}</option>)}
                  </select>
                  <input value={form.request_number || ''}
                    placeholder="(auto)"
                    onChange={e => setField('request_number', e.target.value)}
                    readOnly={!isNew && !!form.request_number}
                    className={`${inp} flex-1 min-w-0 ${!isNew ? 'bg-gray-50' : ''}`} />
                </div>
              </div>
              <div className="w-28 shrink-0">
                <label className={lbl}>Status</label>
                <select value={form.status} onChange={e => setField('status', e.target.value)}
                  disabled={isReadOnly} className={sel}>
                  {Object.keys(STATUS_COLORS).map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>Posting Date</label>
                <input type="date" value={form.posting_date} onChange={e => setField('posting_date', e.target.value)}
                  disabled={isReadOnly} className={inp} />
              </div>
              <div>
                <label className={lbl}>Due Date</label>
                <input type="date" value={form.due_date} onChange={e => setField('due_date', e.target.value)}
                  disabled={isReadOnly} className={inp} />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className={lbl}>Document Date</label>
                <input type="date" value={form.document_date} onChange={e => setField('document_date', e.target.value)}
                  disabled={isReadOnly} className={inp} />
              </div>
              <div>
                <label className={lbl}>Referenced Document</label>
                <div className="flex items-center gap-1">
                  <button onClick={() => setShowRefInfo(true)}
                    className="h-[28px] px-2 border border-sap-border rounded text-xs text-sap-textLight hover:bg-gray-50 bg-white">…</button>
                </div>
              </div>
            </div>

            {/* From / To Warehouse with yellow arrows → open warehouse picker */}
            <div className="pt-1 space-y-1.5 bg-sap-blue/3 rounded-lg p-2 border border-sap-border/40">
              <div className="flex items-center gap-2">
                <span className={`${lbl} w-28 mb-0`}>From Warehouse</span>
                <div className="flex items-center gap-1 flex-1">
                  <YellowArrow title="Choose From Warehouse"
                    onClick={() => setWhPicker({ field: 'from' })} />
                  <input
                    value={form.from_wh_code}
                    onChange={e => {
                      setField('from_wh_code', e.target.value);
                      const wh = warehouses.find(w => w.wh_code === e.target.value);
                      if (wh) setField('from_warehouse_id', String(wh.wh_id));
                    }}
                    disabled={isReadOnly}
                    className={`${inp} w-16 font-mono`} placeholder="01"
                  />
                  <button disabled className="h-[28px] px-1.5 border border-sap-border rounded text-xs text-sap-textLight bg-gray-50 cursor-default">□</button>
                  <span className="text-xs text-sap-textLight truncate max-w-[100px]">
                    {warehouses.find(w => String(w.wh_id) === String(form.from_warehouse_id))?.wh_name || ''}
                  </span>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={`${lbl} w-28 mb-0`}>To Warehouse</span>
                <div className="flex items-center gap-1 flex-1">
                  <YellowArrow title="Choose To Warehouse"
                    onClick={() => setWhPicker({ field: 'to' })} />
                  <input
                    value={form.to_wh_code}
                    onChange={e => {
                      setField('to_wh_code', e.target.value);
                      const wh = warehouses.find(w => w.wh_code === e.target.value);
                      if (wh) setField('to_warehouse_id', String(wh.wh_id));
                    }}
                    disabled={isReadOnly}
                    className={`${inp} flex-1 font-mono`} placeholder="01"
                  />
                  <span className="text-xs text-sap-textLight truncate max-w-[100px]">
                    {warehouses.find(w => String(w.wh_id) === String(form.to_warehouse_id))?.wh_name || ''}
                  </span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex border-b border-sap-border/30 bg-[#F7F5F2]">
          {['Contents', 'Attachments'].map(t => (
            <button key={t} onClick={() => setTab(t)}
              className={`px-5 py-2 text-xs font-medium transition-colors border-b-2
                ${tab === t
                  ? 'border-sap-blue text-sap-blue bg-white'
                  : 'border-transparent text-sap-textLight hover:text-sap-text hover:bg-white/50'}`}>
              {t}
            </button>
          ))}
        </div>

        {/* ══ Contents Tab ══ */}
        {tab === 'Contents' && (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="bg-sap-blue/5 border-b border-sap-border/40 text-sap-textLight">
                  <th className="text-center px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-8">#</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-32">Item No.</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider min-w-[140px]">Item Description</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">From Warehouse</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">To Warehouse</th>
                  <th className="text-right px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-20">Quantity</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-14">UoM Code</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-20">UoM Name</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Product Group</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-20">Period</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Requester</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Request</th>
                  <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-24">Report</th>
                  {!isReadOnly && <th className="w-7"></th>}
                </tr>
              </thead>
              <tbody>
                {lines.map((l, idx) => (
                  <tr key={l._key || idx}
                    className={`border-t border-sap-border/20 ${idx % 2 === 0 ? 'bg-white' : 'bg-gray-50/30'}`}>
                    {/* # */}
                    <td className="text-center px-2 py-1 text-sap-textLight font-mono">{idx + 1}</td>
                    {/* Item No. */}
                    <td className="px-1 py-1">
                      <input value={l.item_no}
                        onChange={e => updateLine(idx, 'item_no', e.target.value)}
                        disabled={isReadOnly}
                        className="w-24 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue font-mono" />
                    </td>
                    {/* Item Description */}
                    <td className="px-1 py-1">
                      <input value={l.item_description}
                        onChange={e => updateLine(idx, 'item_description', e.target.value)}
                        disabled={isReadOnly}
                        className="w-full h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* From Warehouse */}
                    <td className="px-1 py-1">
                      <div className="flex items-center gap-0.5 cursor-pointer" onClick={() => !isReadOnly && setWhPicker({ field: 'line_from', lineIdx: idx })}>
                        {!isReadOnly && <YellowArrow title="Choose From Warehouse" onClick={e => { e.stopPropagation(); setWhPicker({ field: 'line_from', lineIdx: idx }); }} />}
                        <span className="text-xs text-sap-text font-mono">
                          {l.from_wh_code || form.from_wh_code || '—'}
                        </span>
                      </div>
                    </td>
                    {/* To Warehouse */}
                    <td className="px-1 py-1">
                      <div className="flex items-center gap-0.5">
                        {!isReadOnly && <YellowArrow title="Choose To Warehouse" onClick={() => setWhPicker({ field: 'line_to', lineIdx: idx })} />}
                        <span className="text-xs text-sap-blue font-mono font-semibold">
                          {l.to_wh_code || (form.to_wh_code ? <span className="text-sap-textLight/60 italic">{form.to_wh_code}</span> : '—')}
                        </span>
                      </div>
                    </td>
                    {/* Quantity */}
                    <td className="px-1 py-1">
                      <input type="number" min="0" step="0.0001" value={l.quantity}
                        onChange={e => updateLine(idx, 'quantity', e.target.value)}
                        disabled={isReadOnly}
                        className="w-16 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue text-right" />
                    </td>
                    {/* UoM Code */}
                    <td className="px-1 py-1">
                      <input value={l.uom_code} onChange={e => updateLine(idx, 'uom_code', e.target.value)}
                        disabled={isReadOnly}
                        className="w-12 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* UoM Name */}
                    <td className="px-1 py-1">
                      <input value={l.uom_name} onChange={e => updateLine(idx, 'uom_name', e.target.value)}
                        disabled={isReadOnly}
                        className="w-18 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Product Group */}
                    <td className="px-1 py-1">
                      <input value={l.product_group} onChange={e => updateLine(idx, 'product_group', e.target.value)}
                        disabled={isReadOnly}
                        className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Period */}
                    <td className="px-1 py-1">
                      <input value={l.period} onChange={e => updateLine(idx, 'period', e.target.value)}
                        disabled={isReadOnly}
                        className="w-16 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Requester */}
                    <td className="px-1 py-1">
                      <input value={l.requester} onChange={e => updateLine(idx, 'requester', e.target.value)}
                        disabled={isReadOnly}
                        className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Request */}
                    <td className="px-1 py-1">
                      <input value={l.request_ref} onChange={e => updateLine(idx, 'request_ref', e.target.value)}
                        disabled={isReadOnly}
                        className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Report */}
                    <td className="px-1 py-1">
                      <input value={l.report} onChange={e => updateLine(idx, 'report', e.target.value)}
                        disabled={isReadOnly}
                        className="w-20 h-[26px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue" />
                    </td>
                    {/* Remove */}
                    {!isReadOnly && (
                      <td className="px-1 py-1 text-center">
                        <button onClick={() => removeLine(idx)}
                          className="text-red-400 hover:text-red-600 text-base leading-none transition-colors">×</button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
            {!isReadOnly && (
              <div className="px-3 py-2 border-t border-sap-border/20">
                <button onClick={addLine}
                  className="text-xs text-sap-blue hover:text-sap-blueDark font-semibold transition-colors">
                  + Add Row
                </button>
              </div>
            )}
          </div>
        )}

        {/* ══ Attachments Tab ══ */}
        {tab === 'Attachments' && (
          <div className="flex gap-0">
            {/* Table area */}
            <div className="flex-1 min-h-[180px] border-r border-sap-border/30">
              <table className="w-full text-xs">
                <thead>
                  <tr className="bg-[#E8E4DC] border-b border-sap-border/40">
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-8">#</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-48">Target Path</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-48">File Name</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-32">Attachment Date</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider">Free Text</th>
                    <th className="text-left px-2 py-1.5 font-semibold text-[10px] uppercase tracking-wider w-32">Copy to Target Document</th>
                  </tr>
                </thead>
                <tbody>
                  {attachments.length === 0 && (
                    <tr><td colSpan={6} className="px-2 py-6 text-center text-sap-textLight">No attachments</td></tr>
                  )}
                  {attachments.map((att, idx) => (
                    <tr key={att.id}
                      onClick={() => setSelectedAttIdx(idx)}
                      className={`cursor-pointer border-b border-sap-border/20 ${
                        selectedAttIdx === idx ? 'bg-blue-50 border-l-2 border-l-sap-blue' : 'hover:bg-gray-50'
                      }`}>
                      <td className="px-2 py-1 text-sap-textLight">{idx + 1}</td>
                      <td className="px-2 py-1 text-sap-textLight truncate max-w-[12rem]" title={att.targetPath}>{att.targetPath}</td>
                      <td className="px-2 py-1 text-sap-blue font-medium truncate max-w-[12rem]" title={att.fileName}>{att.fileName}</td>
                      <td className="px-2 py-1 text-sap-textLight">{att.attachmentDate}</td>
                      <td className="px-2 py-1">
                        <input
                          value={att.freeText}
                          onChange={e => {
                            const updated = [...attachments];
                            updated[idx] = { ...updated[idx], freeText: e.target.value };
                            setAttachments(updated);
                          }}
                          className="w-full h-[22px] px-1 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
                        />
                      </td>
                      <td className="px-2 py-1 text-center">
                        <input type="checkbox"
                          checked={att.copyToTarget}
                          onChange={e => {
                            const updated = [...attachments];
                            updated[idx] = { ...updated[idx], copyToTarget: e.target.checked };
                            setAttachments(updated);
                          }}
                          className="accent-sap-blue"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {/* Right-side buttons */}
            <div className="flex flex-col gap-1.5 p-2 bg-[#F2F0EC] border-l border-sap-border/30">
              <input ref={attInputRef} type="file" className="hidden"
                onChange={e => {
                  const file = e.target.files[0];
                  if (!file) return;
                  const todayStr = new Date().toISOString().slice(0, 10);
                  setAttachments(prev => [...prev, {
                    id: Date.now(),
                    targetPath: '',
                    fileName: file.name,
                    attachmentDate: todayStr,
                    freeText: '',
                    copyToTarget: false,
                    file,
                  }]);
                  e.target.value = '';
                }}
              />
              <button
                onClick={() => attInputRef.current?.click()}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center">
                Browse
              </button>
              <button
                disabled={selectedAttIdx === null}
                onClick={() => {
                  if (selectedAttIdx === null) return;
                  const att = attachments[selectedAttIdx];
                  if (att.file) {
                    const url = URL.createObjectURL(att.file);
                    window.open(url, '_blank');
                  }
                }}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center disabled:opacity-50 disabled:cursor-default">
                Display
              </button>
              <button
                disabled={selectedAttIdx === null}
                onClick={() => {
                  if (selectedAttIdx === null) return;
                  const updated = attachments.filter((_, i) => i !== selectedAttIdx);
                  setAttachments(updated);
                  setSelectedAttIdx(null);
                }}
                className="px-4 py-1 text-xs border border-sap-border rounded bg-[#E0DDD8] hover:bg-[#d0cdc8] text-sap-text font-semibold w-20 text-center disabled:opacity-50 disabled:cursor-default">
                Delete
              </button>
            </div>
          </div>
        )}

        {/* ── Footer ── */}
        <div className="grid grid-cols-2 gap-4 p-4 bg-[#F7F5F2] border-t border-sap-border/30">
          <div className="space-y-2">
            <div>
              <label className={lbl}>Sales Employee</label>
              <div className="flex items-center gap-1">
                <input value={form.sales_employee} onChange={e => setField('sales_employee', e.target.value)}
                  disabled={isReadOnly} placeholder="-No Sales Employee-" className={`${inp} flex-1`} />
                {!isReadOnly && (
                  <button type="button" onClick={() => setShowSalesPicker(true)}
                    title="Choose Sales Employee"
                    className="flex-shrink-0 w-[28px] h-[28px] flex items-center justify-center border border-sap-border rounded bg-white hover:bg-gray-50 text-sap-textLight transition-colors">
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="4" y1="6" x2="20" y2="6" />
                      <line x1="4" y1="12" x2="20" y2="12" />
                      <line x1="4" y1="18" x2="20" y2="18" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className={lbl}>Journal Remarks</label>
              <input value={form.journal_remarks} onChange={e => setField('journal_remarks', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
          </div>
          <div className="space-y-2">
            <div>
              <label className={lbl}>Pick and Pack Remarks</label>
              <input value={form.pick_and_pack_remarks} onChange={e => setField('pick_and_pack_remarks', e.target.value)}
                disabled={isReadOnly} className={inp} />
            </div>
            <div>
              <label className={lbl}>Remarks</label>
              <textarea value={form.remarks} onChange={e => setField('remarks', e.target.value)}
                disabled={isReadOnly} rows={2}
                className="w-full px-2 py-1 text-xs border border-sap-border rounded bg-white focus:outline-none focus:border-sap-blue resize-none" />
            </div>
          </div>
        </div>

        {/* ── Action buttons ── */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-sap-border/30 bg-white">
          <div className="flex items-center gap-2">
            {!isReadOnly && (
              <>
                <button onClick={() => handleSave('Open')} disabled={saving}
                  className="px-5 py-1.5 text-xs bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark font-semibold transition-colors disabled:opacity-60">
                  {saving ? 'Saving…' : 'Add'}
                </button>
                <button onClick={() => handleSave('Draft')} disabled={saving}
                  className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 transition-colors disabled:opacity-60">
                  Save Draft
                </button>
              </>
            )}
            <button onClick={() => navigate('/inventory/transfer-requests')}
              className="px-4 py-1.5 text-xs border border-sap-border rounded-lg text-sap-text hover:bg-gray-50 transition-colors">
              Cancel
            </button>
          </div>
          {/* Copy To button (bottom right — copies this request to an Inventory Transfer)
               Visible always; disabled/grey on new or closed docs, active on saved Open docs */}
          <button
            disabled={isNew || isReadOnly}
            onClick={() => navigate(`/inventory/transfer/new?copyFrom=itr&id=${id}`)}
            className={`px-4 py-1.5 text-xs font-semibold rounded-lg transition-colors ${
              isNew || isReadOnly
                ? 'border border-gray-200 text-gray-400 bg-gray-50 cursor-not-allowed'
                : 'bg-sap-blue text-white hover:bg-sap-blueDark'
            }`}>
            Copy To
          </button>
        </div>
      </div>

      {/* ── Warehouse picker ── */}
      {whPicker && (
        <WarehousePickerDialog
          warehouses={warehouses}
          currentWhId={getPickerCurrentId()}
          label={whPicker.field === 'from' || whPicker.field === 'line_from' ? 'From Warehouse' : 'To Warehouse'}
          onSelect={handleWhSelect}
          onClose={() => setWhPicker(null)}
        />
      )}

      {/* ── Item picker ── */}
      {itemPickerIdx !== null && (
        <ItemPickerDialog
          onSelect={handleItemSelect}
          onClose={() => setItemPickerIdx(null)}
        />
      )}

      {/* ── Reference Information dialog ── */}
      {showRefInfo && (
        <ReferenceInfoDialog
          refsTo={refsTo} refsBy={refsBy}
          onSave={({ refsTo: t, refsBy: b }) => { setRefsTo(t); setRefsBy(b); }}
          onClose={() => setShowRefInfo(false)}
        />
      )}

      {/* ── Sales employee picker ── */}
      {showSalesPicker && (
        <SalesEmployeePickerDialog
          current={form.sales_employee}
          onSelect={(name) => setField('sales_employee', name)}
          onClose={() => setShowSalesPicker(false)}
        />
      )}
    </div>
  );
}
